import { createContext, type ReactNode, useContext, useMemo, useSyncExternalStore } from "react";
import { type AppLocale, getIntlLocale, getLocale, type MessageValues, setLocale, subscribeToLocale, type TranslationKey, translate } from "./i18n";

type I18nContextValue = {
  intlLocale: string;
  locale: AppLocale;
  setLocale: typeof setLocale;
  t: (key: TranslationKey, values?: MessageValues) => string;
};

const I18nContext = createContext<I18nContextValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const locale = useSyncExternalStore(subscribeToLocale, getLocale, getLocale);
  const value = useMemo<I18nContextValue>(
    () => ({
      intlLocale: getIntlLocale(locale),
      locale,
      setLocale,
      t: (key, values) => translate(key, values, locale),
    }),
    [locale],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const value = useContext(I18nContext);
  if (!value) {
    throw new Error("useI18n must be used inside I18nProvider.");
  }
  return value;
}
